// Studio app — root. CD owns the whole app (shell + screens); this is the
// deployable frontend. Data comes from @v0y4ge/api-client; design system from
// @v0y4ge/jrni-ui + jrni-tokens (CD-owned). No static seed data in the render path.

import "@v0y4ge/jrni-tokens/tokens.css";
import "@v0y4ge/jrni-tokens/reset.css";
// App-layout rules (route-shell max-width override) — deliberately OUTSIDE the
// token stylesheets so DS re-emits can never delete them (DS round-trip ①).
import "./styles/studio-app.css";
import { useEffect, useRef } from "react";
import { NavProvider, useNav, isPublicAttendRoute } from "./shell/router";
import { AppRouter } from "./shell/AppRouter";
import { ToastHost } from "./shell/toast";
import { useAuth, signIn, landingRouteForRole, getUser } from "./shell/access";
import Signin from "./shell/Signin";
import { ImpersonationBanner } from "./shell/ImpersonationBanner";
import { useDateTimeVersion } from "./lib/datetime";
import { useStaffTimezoneSync } from "./screens/staff/useStaffTimezone";
import { ErrorBoundary } from "./shell/ErrorBoundary";

// Deep-link params captured at MODULE LOAD — before <NavProvider> mounts and
// rewrites the URL to a route (which would drop our fragment). Read from the
// hash (survives the preview host's path rewrite) with a query fallback.
const DEEPLINK: { persona: string; route: string | null } | null = (function () {
  if (typeof window === "undefined") return null;
  const raw = (window.location.hash || "").replace(/^#/, "") || (window.location.search || "").replace(/^\?/, "");
  const q = new URLSearchParams(raw);
  const persona = q.get("persona");
  return persona ? { persona, route: q.get("route") } : null;
})();

function AuthGate() {
  // Re-render the whole screen tree when locale/timezone changes so every
  // date/time re-formats live.
  useDateTimeVersion();
  // The signed-in staff member's own profile zone becomes the display zone
  // (explicit user preference still wins) — 2026-08-07, Pat.
  useStaffTimezoneSync();
  const { signedIn } = useAuth();
  const { route, go } = useNav();
  // Deep-link auto-sign-in (demo): a link like index.html?persona=<id>&route=<routeId>
  // (used by the RFE board's login tags) mints that persona's session and lands
  // on the target route, skipping the org/persona chooser. // ui-config
  const booted = useRef(false);
  useEffect(() => {
    if (booted.current || !DEEPLINK || signedIn) return;
    booted.current = true;
    signIn(DEEPLINK.persona).then(() => {
      const u = getUser();
      const a = u.assignments || {};
      const role = a.workforce || a.storeops || a.events || u.role;
      go(DEEPLINK.route || landingRouteForRole(role));
    }).catch(() => {});
  }, [signedIn, go]);
  // 2026-07-14 docket (attend-host flip): a public event route on the attend
  // host renders ANONYMOUSLY — no session, no persona chooser. attend.<tenant>
  // is the public WYSIWYG face of a published event; studio.<tenant> (and every
  // operator route on any host) stays SSO-gated exactly as before.
  if (isPublicAttendRoute(route)) return <AppRouter />;
  if (!signedIn) return <Signin />;
  /* The acting-as banner mounts HERE, at the app root, not in `Screen`.
     Mounted in `Screen` it missed every view that doesn't use it — including
     the app launcher's "No apps are enabled for your role yet" shell, which is
     exactly where an impersonated narrow-role persona lands. The admin was left
     with no banner and no exit but Sign out, which is the whole bug this was
     meant to fix. The narrower the target's role, the more certain the banner
     was to be missing. */
  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
      <ImpersonationBanner />
      <div style={{ flex: 1, minHeight: 0 }}><AppRouter /></div>
    </div>
  );
}

export default function App() {
  return (
    <div data-jrni-component="studio-app-root" style={{ height: "100vh", overflow: "hidden", background: "var(--jrni-color-surface-canvas)" }}>
      {/* Last-resort boundary: catches anything thrown OUTSIDE a screen (the
          rail, topbar, router or auth gate itself) so the app can never render
          a bare grey page with no way back. Per-screen boundaries live in
          AppRouter and handle the common case without losing the chrome. */}
      <ErrorBoundary scope="app" label="Studio" variant="app">
        <NavProvider initialRoute="dashboard">
          <AuthGate />
        </NavProvider>
      </ErrorBoundary>
      <ToastHost />
    </div>
  );
}
